import Dashboard from "../../layout/Dashboard";
import { Icon } from '@iconify/react'
import { ListHead, ListRow, NavContainer, NavWrapper, Switch, Ticker } from "../../styles/CrypoAssetStyle";
import { Content, Heading } from "../../styles/OverviewStyle";
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from "react-router";
import styled, { keyframes } from 'styled-components';
import type { GiftcardAsset } from '../../../types/ApiTypes';

type SellAsset = GiftcardAsset & { id?: string | undefined }
import AddGiftcardSidebar from '../../components/AddGiftcardSidebar'
import { giftcardService } from '../../services/GiftcardService';

const shimmer = keyframes`
   0% { background-position: -468px 0; }
   100% { background-position: 468px 0; }
`;

const SkeletonBox = styled.div`
   background: linear-gradient(to right, #F0F0F2 8%, #E7E7E9 18%, #F0F0F2 33%);
   background-size: 800px 104px;
   animation: ${shimmer} 1.5s linear infinite;
   border-radius: 6px;
   height: 14px;
`;

const SkeletonRow = styled(ListRow)`
   .icon-skeleton { width: 20px; height: 20px; border-radius: 999px; }
   .name-skeleton { width: 120px; }
   .regions-skeleton { width: 200px; }
   .switch-skeleton { width: 28px; height: 17px; border-radius: 999px; }
`;

export default function SellGiftcardAssetManagement() {
   const navigate = useNavigate()
   const [query, setQuery] = useState('')
   const [cards, setCards] = useState<SellAsset[]>([])
   const [loading, setLoading] = useState(true)
   const [showAdd, setShowAdd] = useState<{ open: boolean; id: string; regions: string[] }>({ open: false, id: '', regions: [] })

   useEffect(() => {
      loadAssets()
   }, [])

   const loadAssets = async () => {
      try {
         const [assetsRes, ratesRes] = await Promise.all([
            giftcardService.getSellAssets(),
            giftcardService.getSellRates()
         ])
         
         if (assetsRes.status === 'success' && assetsRes.data && ratesRes.status === 'success' && ratesRes.data) {
            const merged = assetsRes.data.map(asset => {
               const rate = ratesRes.data!.find(r => r.type === asset.type)
               return { ...asset, id: rate?.id }
            })
            setCards(merged)
         }
      } catch (error) {
         console.error('Failed to load sell assets:', error)
      } finally {
         setLoading(false)
      }
   }

   const filtered = useMemo(() => {
      const q = query.toLowerCase()
      if (!q) return cards
      return cards.filter(c => c.type.toLowerCase().includes(q) || c.regions?.some(r => r.countryCode.toLowerCase().includes(q)))
   }, [cards, query])

   const toggle = async (type: string) => {
      try {
         const response = await giftcardService.toggleSellAsset(type)
         if (response.status === 'success' && response.data) {
            setCards(list => list.map(c => c.type === type ? { ...c, isEnabled: response.data!.enabled } : c))
         }
      } catch (error) {
         console.error('Failed to toggle asset:', error)
      }
   }

   const handleRegionUpdate = () => {
      loadAssets()
      setShowAdd({ open: false, id: '', regions: [] })
   }

   return (
      <Dashboard title="Asset Management / Sell Giftcard" icon="mdi:wallet-giftcard" active="asset_management">
         <Content>
            <div className="flex items-center justify-between">
               <Heading>Asset Management</Heading>
            </div>
            <NavContainer>
               <NavWrapper children="Crypto" onClick={() => navigate('/assets')} />
               <NavWrapper children="Buy Giftcard" onClick={() => navigate('/assets/giftcard/buy')} />
               <NavWrapper children="Sell Giftcard" $active />
            </NavContainer>

            <div className="flex items-center gap-4 mt-2 mb-3">
               <div className="flex items-center gap-2 bg-white border border-[#EDEDEE] rounded-md px-[9px] py-[6px] w-[229px] h-[28px]">
                  <Icon icon='solar:magnifer-linear' width={14} className="text-[#B3B3B6] mb-0.5" />
                  <input
                     value={query}
                     onChange={e => setQuery(e.target.value)}
                     placeholder="Search"
                     className="flex-1 bg-transparent outline-none text-[12px] font-medium tracking-tight placeholder:text-[#B3B3B6]" />
               </div>
            </div>

            <ListHead style={{ gridTemplateColumns: '1fr 1fr 90px' }}>
               <div>Card Name</div>
               <div>Regions</div>
               <div className="text-right pr-2">Enabled</div>
            </ListHead>

            {loading ? (
               [...Array(5)].map((_, i) => (
                  <SkeletonRow key={i} style={{ gridTemplateColumns: '1fr 1fr 90px' }}>
                     <div className="flex items-center gap-3">
                        <SkeletonBox className="icon-skeleton" />
                        <SkeletonBox className="name-skeleton" />
                     </div>
                     <div className="flex items-center gap-2">
                        <SkeletonBox className="regions-skeleton" />
                     </div>
                     <div className="flex justify-end">
                        <SkeletonBox className="switch-skeleton" />
                     </div>
                  </SkeletonRow>
               ))
            ) : filtered.length === 0 ? (
               <div className="text-center py-8 text-[#B3B3B6]">No assets found</div>
            ) : (
               filtered.map(card => {
                  const regions = card.regions || []
                  const displayRegions = regions.slice(0, 3)
                  const extra = regions.length > 3 ? `+${regions.length - 3}` : null
                  const regionCodes = regions.map(r => r.countryCode)
                  
                  return (
                     <ListRow key={card.type} style={{ gridTemplateColumns: '1fr 1fr 90px' }}>
                        <div className="flex items-center gap-3">
                           <span className="w-5 h-5 inline-flex items-center justify-center rounded-full bg-[#F1F1F3]">
                              <img src={card.logoUrl} alt={card.type} className="w-3.5 h-3.5 object-contain" />
                           </span>
                           <span className="text-sm text-[#101018]">{card.type}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                           {displayRegions.map(r => <Ticker key={r.countryCode} children={r.countryName} />)}
                           {extra && <Ticker>{extra}</Ticker>}
                           <Icon icon='tabler:edit' width={16} onClick={() => setShowAdd({ open: true, id: card.id || '', regions: regionCodes })} className="ml-3 text-[#7e7e7e] cursor-pointer" />
                        </div>
                        <div className="flex justify-end">
                           <Switch $on={card.isEnabled} onClick={() => toggle(card.type)} aria-pressed={card.isEnabled} aria-label={`Toggle ${card.type}`} />
                        </div>
                     </ListRow>
                  )
               })
            )}

            <AddGiftcardSidebar open={showAdd} onClose={() => setShowAdd({ open: false, id: '', regions: [] })} onSave={handleRegionUpdate} />
         </Content>
      </Dashboard>
   )
}